/* eslint-disable complexity -- source-parity download flow */
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import type { FileMetadata, ProgressCallback } from '@/types/poc/helia';
import { setDecapsKey } from '@/store/slices/auth/authSlice';
import { hexToBytes } from '@/utils/hex.utils';
import { ensureFileCloned } from '@/store/thunks/poc/cloneQueue';

const HEX_BASE = 16;
const HEX_PAD_LENGTH = 2;

// Timeout for fetching legacy encryption CID data from Helia
const ENCRYPTION_FETCH_TIMEOUT_MS = 30_000;

// Progress constants
const PROGRESS_KEY_FETCH = 10;
const PROGRESS_ENCRYPTION_FETCH = 15;
const PROGRESS_KEY_READY = 25;
const PROGRESS_CLONE_STARTED = 35;
const PROGRESS_FILE_READ = 60;
const PROGRESS_FILE_DECRYPT = 75;
const PROGRESS_COMPLETE = 100;

/**
 * Shape of the encryption data JSON stored in Helia
 */
interface EncryptionDataFromHelia {
  recipientId?: string;
  cipherText: number[];
  encryptedFileKey: {
    encryptedData: number[];
    iv: number[];
    authTag: number[];
  };
}

function isEncryptionDataFromHelia(
  value: unknown
): value is EncryptionDataFromHelia {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const maybeValue = value as Partial<EncryptionDataFromHelia>;
  const encryptedFileKey = maybeValue.encryptedFileKey;
  return (
    Array.isArray(maybeValue.cipherText) &&
    typeof encryptedFileKey === 'object' &&
    Array.isArray(encryptedFileKey.encryptedData) &&
    Array.isArray(encryptedFileKey.iv) &&
    Array.isArray(encryptedFileKey.authTag)
  );
}

/**
 * Download and decrypt an encrypted file as a recipient.
 *
 * Flow:
 * 1. Validate user auth and resolve encryption data
 * 2. Ensure file is cloned locally (via ensureFileCloned — handles preemptive cloning)
 * 3. Read encrypted file from local Helia blockstore (fast, no network)
 * 4. Decrypt and return
 */
export const downloadAndDecryptFile = createAppAsyncThunk(
  'helia/downloadAndDecrypt',
  async(
    {
      sixId,
      fileMetadata,
      onProgress
    }: {
      sixId: string;
      fileMetadata: FileMetadata;
      onProgress?: ProgressCallback;
    },
    { extra: { pocServices, userService, sixDetailService, loggerService }, dispatch, getState, rejectWithValue }
  ) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:p2p:files:download' });

    try {
      log.info('Starting download for file:', fileMetadata.name, '(CID:', fileMetadata.cid, ')');

      // Step 1: Validate user
      log.debug('Step 1: Validating user and permissions...');
      const state = getState();

      if (state.user.user?.id == null) {
        log.error('User not authenticated');
        throw new Error('User not authenticated');
      }

      const currentUserId = state.auth.userId ?? state.user.user.id;

      // Step 2: Resolve encryption data
      // recipientEncryption (primary, from P2P metadata) or decryptionCIDS (legacy, fetched from Helia by CID)
      log.debug('Step 2: Resolving encryption data...');
      let ourRecipientData: EncryptionDataFromHelia | null = null;

      if (fileMetadata.recipientEncryption != null && fileMetadata.recipientEncryption.length > 0) {
        log.debug('Found', fileMetadata.recipientEncryption.length, 'recipient(s) in metadata');
        const found = fileMetadata.recipientEncryption.find(
          rec => rec.recipientId === currentUserId
        );
        if (found != null) {
          ourRecipientData = found;
        }
      } else if (fileMetadata.decryptionCIDS != null && fileMetadata.decryptionCIDS.length > 0) {
        log.debug('Found', fileMetadata.decryptionCIDS.length, 'decryption CID(s), fetching from Helia...');

        const ourDecryptionCID = fileMetadata.decryptionCIDS.find(
          entry => entry.userId === currentUserId
        );

        if (ourDecryptionCID != null) {
          log.debug('Fetching encryption data from CID:', ourDecryptionCID.cid);
          if (onProgress != null) onProgress(PROGRESS_ENCRYPTION_FETCH, PROGRESS_COMPLETE);

          const helia = pocServices.p2pStackManagerService.getHeliaNode(sixId);
          if (helia === null) {
            throw new Error('Helia node not initialized - cannot fetch encryption data');
          }

          try {
            const encryptionJsonBytes = await pocServices.fileStoreService.getFile(
              helia,
              ourDecryptionCID.cid,
              { signal: AbortSignal.timeout(ENCRYPTION_FETCH_TIMEOUT_MS) }
            );
            const encryptionJsonString = new TextDecoder().decode(new Uint8Array(encryptionJsonBytes));
            const parsedEncryptionData: unknown = JSON.parse(encryptionJsonString);
            if (!isEncryptionDataFromHelia(parsedEncryptionData)) {
              throw new Error('Invalid encryption data shape');
            }
            ourRecipientData = {
              ...parsedEncryptionData,
              recipientId: parsedEncryptionData.recipientId ?? currentUserId,
            };
            log.debug('Fetched encryption data from Helia for user:', ourRecipientData.recipientId);
          } catch (fetchError) {
            log.error('Failed to fetch encryption data from Helia:', fetchError);
            throw new Error(`Failed to fetch encryption data: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
          }
        }
      } else {
        throw new Error('No encryption data found for this file');
      }

      if (ourRecipientData == null) {
        log.error('Current user is not a recipient');
        throw new Error('Current user is not a recipient of this file');
      }
      log.debug('Found encryption data for current user');

      // Step 3: Ensure we have the decrypted private key
      log.debug('Step 3: Getting decryption key...');
      let recipientPrivateKey: Uint8Array = new Uint8Array();

      if (state.auth.decapsKey.length > 0) {
        log.debug('Using cached private key');
        recipientPrivateKey = hexToBytes(state.auth.decapsKey);
      } else {
        log.debug('No cached key, fetching and decrypting...');
        if (onProgress != null) onProgress(PROGRESS_KEY_FETCH, PROGRESS_COMPLETE);

        const ukek = state.auth.ukek;

        if (ukek.length === 0) {
          log.error('UKEK not available');
          throw new Error('User encryption key (UKEK) not available for private key decryption');
        }

        try {
          log.debug('Decrypting private key using stored UKEK...');
          const { decapsKey: encryptedPrivateKeyHex } = await userService.getDecapsKey(currentUserId);
          const ukekBytes = hexToBytes(ukek);

          recipientPrivateKey = await pocServices.cryptoService.decryptPrivateKey(
            encryptedPrivateKeyHex,
            ukekBytes
          );
          log.debug('Private key decrypted successfully');

          // Cache for future use
          const decryptedPrivateKeyHex = Array.from(recipientPrivateKey)
            .map(b => b.toString(HEX_BASE).padStart(HEX_PAD_LENGTH, '0'))
            .join('');
          dispatch(setDecapsKey(decryptedPrivateKeyHex));
          log.debug('Private key cached');

        } catch (keyError) {
          log.error('Private key decryption failed:', keyError);
          throw new Error(`Private key decryption failed: ${keyError instanceof Error ? keyError.message : 'Unknown error'}`);
        }
      }

      if (onProgress != null) onProgress(PROGRESS_KEY_READY, PROGRESS_COMPLETE);

      // Step 4: Ensure file is cloned locally (handles preemptive cloning)
      log.debug('Step 4: Ensuring file is cloned locally...');
      if (onProgress != null) onProgress(PROGRESS_CLONE_STARTED, PROGRESS_COMPLETE);

      await dispatch(ensureFileCloned({ sixId, fileMetadata })).unwrap();
      log.debug('File confirmed local');

      // Step 5: Read encrypted file from local Helia (guaranteed fast — no network)
      log.debug('Step 5: Reading encrypted file from local blockstore...');
      if (onProgress != null) onProgress(PROGRESS_FILE_READ, PROGRESS_COMPLETE);

      const helia = pocServices.p2pStackManagerService.getHeliaNode(sixId);
      if (helia === null) {
        throw new Error('Helia node not initialized');
      }

      const encryptedFileData = await pocServices.fileStoreService.getFile(helia, fileMetadata.cid);
      log.debug('Read', encryptedFileData.length, 'bytes from local blockstore');

      const encryptedFile = new File([new Uint8Array(encryptedFileData)], fileMetadata.name, {
        type: 'application/octet-stream'
      });

      // Step 6: Decrypt the file
      log.debug('Step 6: Decrypting file...');
      if (onProgress != null) onProgress(PROGRESS_FILE_DECRYPT, PROGRESS_COMPLETE);
      pocServices.cidDownloadLogger.log(fileMetadata.cid, 'decrypting', { fileName: fileMetadata.name });

      const recipientResult = {
        recipientId: ourRecipientData.recipientId ?? currentUserId,
        cipherText: new Uint8Array(ourRecipientData.cipherText),
        encryptedFileKey: {
          encryptedData: new Uint8Array(ourRecipientData.encryptedFileKey.encryptedData),
          iv: new Uint8Array(ourRecipientData.encryptedFileKey.iv),
          authTag: new Uint8Array(ourRecipientData.encryptedFileKey.authTag)
        }
      };

      const decryptionResult = await pocServices.fileEncryptionService.decryptFileAsRecipient(
        encryptedFile,
        recipientResult,
        recipientPrivateKey
      );
      log.debug('File decrypted successfully');

      pocServices.cidDownloadLogger.log(fileMetadata.cid, 'complete', {
        originalSize: decryptionResult.originalFileSize,
        fileName: decryptionResult.originalFileName
      });
      if (onProgress != null) onProgress(PROGRESS_COMPLETE, PROGRESS_COMPLETE);

      // Notify server that file was downloaded (fire-and-forget)
      sixDetailService.markFileDownloaded(sixId, fileMetadata.id)
        .then(() => { log.debug('Marked file as downloaded on server'); })
        .catch((error: unknown) => { log.warn('Failed to mark file as downloaded:', error); });

      log.info('Download and decryption complete!');
      return {
        decryptedFile: decryptionResult.decryptedFile,
        originalFileName: decryptionResult.originalFileName,
        originalFileSize: decryptionResult.originalFileSize,
        originalFileType: decryptionResult.originalFileType
      };

    } catch (error) {
      log.error('Fatal error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error decrypting file';
      return rejectWithValue({ message: errorMessage });
    }
  }
);
